import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "antd";
import { useAuth } from "../../context/AuthContext";
import { useApp } from "../../context/AppContext";
import { useMemo, useEffect, useState } from "react";
import { sidebarItems } from "./sidebarMenu";

const Sidebar = () => {
  const { setPageTitle } = useApp();
  const { logout, auth } = useAuth();
  const { pathname } = useLocation();

  const [openSubMenu, setOpenSubMenu] = useState("");
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar-collapsed", String(!prev));
      return !prev;
    });
  };

  const finalSidebar = useMemo(() => {
    const isSuperAdmin = auth?.sc_id === "0";
    const canManageMaster = auth?.admin?.role === "super_admin";
    const items = sidebarItems.filter(
      (item) => item.name !== "Master Content" || canManageMaster
    );

    if (isSuperAdmin) {
      items.splice(1, 0, { name: "Schools", path: "/schools", icon: "domain" });
    } else if (auth?.sc_id) {
      items.splice(1, 0, {
        name: "My School",
        path: `/schools/detail/${+auth.sc_id}`,
        icon: "domain",
      });
    }
    return items;
  }, [auth?.sc_id, auth?.admin?.role]);

  useEffect(() => {
    finalSidebar.forEach((item) => {
      if (item.children?.some((sub) => pathname.startsWith(sub.path))) {
        setOpenSubMenu(item.name);
      }
    });

    const currentItem = finalSidebar
      .flatMap((i) => (i.children ? [i, ...i.children] : [i]))
      .find((item) =>
        item.path === "/" ? pathname === "/" : pathname.startsWith(item.path)
      );

    if (currentItem) setPageTitle(currentItem.name);
  }, [pathname, finalSidebar, setPageTitle]);

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  const toggleSubMenu = (name) => {
    setOpenSubMenu(openSubMenu === name ? "" : name);
  };

  return (
    <aside
      className={`overflow-y-auto flex-shrink-0 bg-white dark:bg-[#1C2431] border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-200 ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-b border-gray-200 dark:border-gray-700 h-16 px-3 ${
          collapsed ? "justify-center" : "justify-between gap-2"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="bg-primary rounded-lg w-10 h-10 flex items-center justify-center text-white flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
          >
            <span className="material-symbols-outlined leading-none">school</span>
          </button>
          {!collapsed && (
            <div className="flex flex-col min-w-0">
              <h1 className="text-base font-bold text-[#111318] dark:text-white truncate">
                {process.env.REACT_APP_PROJECT_NAME || "School Admin"}
              </h1>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 space-y-1">
        {finalSidebar.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = openSubMenu === item.name;
          const parentActive =
            hasChildren && item.children.some((sub) => isActive(sub.path));

          if (collapsed) {
            return (
              <Tooltip
                key={item.name || item.path}
                title={item.name}
                placement="right"
              >
                <Link
                  to={hasChildren ? item.children[0].path : item.path}
                  className={`flex items-center justify-center p-2 rounded-lg transition-all ${
                    isActive(item.path) || parentActive
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                </Link>
              </Tooltip>
            );
          }

          if (hasChildren) {
            return (
              <div key={item.name} className="space-y-1">
                <button
                  onClick={() => toggleSubMenu(item.name)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                    parentActive
                      ? "text-primary bg-primary/5"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined">
                      {item.icon}
                    </span>
                    <p className="text-sm font-medium">{item.name}</p>
                  </div>
                  <span
                    className={`material-symbols-outlined transition-transform text-sm ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    expand_more
                  </span>
                </button>

                {isExpanded && (
                  <div className="ml-9 space-y-1">
                    {item.children.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive(sub.path)
                            ? "text-primary font-bold"
                            : "text-gray-500 dark:text-gray-400 hover:text-primary"
                        }`}
                      >
                        <p>{sub.name}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                isActive(item.path)
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <p className="text-sm font-medium">{item.name}</p>
            </Link>
          );
        })}
      </nav>

    </aside>
  );
};

export default Sidebar;
